import React, {
  useEffect,
  useState,
} from "react";

import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
} from "firebase/firestore";

import {
  Badge,
  Box,
  Divider,
  Flex,
  Stack,
  Text,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";

import moment from "moment";

import {
  db,
} from "../../../firebase/config";

export default function InternalNotesDashboard({
  projectId,
  currentUser,
}) {
  const [notes, setNotes] =
    useState([]);

  const isAdmin =
    currentUser?.isAdmin === true;

  const cardBg = useColorModeValue(
    "white",
    "gray.800"
  );

  const toast = useToast();

  useEffect(() => {
    if (!projectId || !isAdmin) {
      setNotes([]);
      return undefined;
    }

    const notesQuery = query(
      collection(
        db,
        "internalNotes"
      ),
      where(
        "projectId",
        "==",
        projectId
      ),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(
      notesQuery,
      (snapshot) => {
        const data = snapshot.docs.map(
          (noteDocument) => ({
            id: noteDocument.id,
            ...noteDocument.data(),
          })
        );

        setNotes(data);
      },
      (error) => {
        console.error(
          "Internal notes listener failed:",
          error
        );

        toast({
          title:
            "Internal notes could not be loaded",
          description:
            "Please refresh the page. If the problem continues, contact an administrator.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    );

    return unsubscribe;
  }, [
    projectId,
    isAdmin,
    toast,
  ]);

  if (!isAdmin) {
    return null;
  }

  return (
    <Stack spacing={4}>
      {notes.length === 0 && (
        <Text color="gray.500">
          No internal notes yet.
        </Text>
      )}

      {notes.map((note) => (
        <Box
          key={note.id}
          p={4}
          bg={cardBg}
          borderRadius="md"
          shadow="sm"
          borderWidth="1px"
        >
          <Text
            fontWeight="semibold"
            mb={2}
          >
            {note.createdBy ||
              "Administrator"}
          </Text>

          {note.title && (
            <Text
              fontWeight="semibold"
              mb={2}
            >
              {note.title}
            </Text>
          )}

          <Text
            mb={3}
            whiteSpace="pre-wrap"
          >
            {note.note ||
              "No note content provided."}
          </Text>

          <Divider mb={2} />

          <Flex
            justify="space-between"
            align="center"
            gap={3}
          >
            <Text
              fontSize="sm"
              color="gray.500"
            >
              {note.createdAt
                ? moment(
                    note.createdAt.toDate?.() ||
                      note.createdAt
                  ).format(
                    "MMMM Do YYYY, h:mm a"
                  )
                : "Unknown time"}
            </Text>

            <Badge colorScheme="purple">
              Internal
            </Badge>
          </Flex>
        </Box>
      ))}
    </Stack>
  );
}